import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { Link, useLocation } from 'react-router-dom'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { X } from 'lucide-react'
import type { NavBadges } from '@/hooks/useNavBadges'
import type { NavItem } from './PortalLayout'
import SideNav from './SideNav'

/**
 * Slide-in navigation for the portal below `xl`, where the fixed sidebar is
 * hidden. Keeps the blue Icewall field: `.side-menu-link` is light-on-blue
 * and would be invisible on anything else.
 *
 * The menu itself is SideNav, shared with the sidebar from its own module so
 * this file never imports PortalLayout at runtime.
 */
export default function PortalDrawer({
  open,
  onClose,
  nav,
  title,
  badges,
}: {
  open: boolean
  onClose: () => void
  nav: NavItem[]
  title: string
  badges: NavBadges
}) {
  const location = useLocation()
  const closeRef = useRef<HTMLButtonElement>(null)
  const reduce = useReducedMotion()

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  // Every link closes the drawer itself, but this also covers the browser back
  // button. Compared against the previous path rather than keyed on it, so the
  // effect cannot fire on mount and close the drawer as it opens.
  const lastPath = useRef(location.pathname)
  useEffect(() => {
    if (lastPath.current === location.pathname) return
    lastPath.current = location.pathname
    onClose()
  }, [location.pathname, onClose])

  // Crossing 1280px brings the fixed sidebar back and this drawer hides itself
  // in CSS. Closing it for real matters because the body scroll lock below
  // would otherwise stay on with nothing visible to explain why.
  useEffect(() => {
    if (!open) return
    const wideEnoughForTheSidebar = window.matchMedia('(min-width: 1280px)')
    const closeIfWide = () => {
      if (wideEnoughForTheSidebar.matches) onClose()
    }
    closeIfWide()
    wideEnoughForTheSidebar.addEventListener('change', closeIfWide)
    return () => wideEnoughForTheSidebar.removeEventListener('change', closeIfWide)
  }, [open, onClose])

  useEffect(() => {
    if (!open) return
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previous
    }
  }, [open])

  // autoFocus is unreliable inside a portal plus AnimatePresence tree.
  useEffect(() => {
    if (open) closeRef.current?.focus()
  }, [open])

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          className="no-print fixed inset-0 z-50 xl:hidden"
          initial={reduce ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: reduce ? 0 : 0.18, ease: 'easeOut' }}
        >
          <div className="absolute inset-0 bg-foreground/50 backdrop-blur-sm" onMouseDown={onClose} />

          <motion.div
            id="portal-drawer"
            className="absolute inset-y-0 left-0 flex w-full max-w-[320px] flex-col bg-primary bg-noise px-5 pt-4 shadow-xl"
            initial={reduce ? false : { x: -24 }}
            animate={{ x: 0 }}
            exit={reduce ? undefined : { x: -24 }}
            transition={{ duration: reduce ? 0 : 0.18, ease: 'easeOut' }}
          >
            <div className="flex items-center gap-3 py-3">
              <Link to="/" title="MPG marketplace" onClick={onClose}>
                <img src="/logo_hd_white.png" alt="MPG Printing" className="h-6 w-auto object-contain" />
              </Link>
              {title && (
                <span className="border-l border-background/20 pl-2.5 text-2xs font-semibold uppercase tracking-wider text-background/50">{title}</span>
              )}
              <button
                type="button"
                ref={closeRef}
                onClick={onClose}
                aria-label="Close menu"
                className="ml-auto flex size-10 items-center justify-center rounded-lg text-background/80 hover:bg-background/10 hover:text-background"
              >
                <X className="size-5" />
              </button>
            </div>

            <nav className="scrollbar-thin min-h-0 flex-1 overflow-y-auto pb-8">
              <SideNav nav={nav} badges={badges} onNavigate={onClose} />
            </nav>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  )
}
